import { getMove } from './Chess';

/**
 * Unified accessor for position data that handles both legacy fen field and new startPosition structure
 * @param {Object} gameData - The game data object
 * @param {string} index - The move index ('-1' for start position, '0'+ for moves)
 * @returns {Object} Position data with consistent structure
 */
export const getPositionData = (gameData, index) => {
  if (index === '-1') {
    // New way: use startPosition if available
    if (gameData.startPosition) {
      return gameData.startPosition;
    }
    
    // Fallback: create move-like object from legacy fen field
    return {
      fen: gameData.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
      content: "",
      arrows: [],
      highlightedSquares: {},
      analysis: { pvs: [], scores: [], classification: "", depth: 0 },
      variations: []  // Add variations support for starting position
    };
  }
  
  // Regular moves use existing logic
  return getMove(gameData.moves, index);
};

/**
 * Update position data for a specific index
 * @param {Object} gameData - The game data object  
 * @param {string} index - The move index ('-1' for start position, '0'+ for moves)
 * @param {Object} updates - Updates to apply to the position
 * @returns {Object} Updated game data
 */
export const updatePositionData = (gameData, index, updates) => {
  if (index === '-1') {
    // Ensure startPosition exists
    if (!gameData.startPosition) {
      gameData.startPosition = {
        fen: gameData.fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
        content: "",
        arrows: [],
        highlightedSquares: {},
        analysis: { pvs: [], scores: [], classification: "", depth: 0 },
        variations: []  // Add variations support for starting position
      };
    }
    
    // Update startPosition
    return {
      ...gameData,
      startPosition: {
        ...gameData.startPosition,
        ...updates
      }
    };
  }
  
  // For regular moves, update the moves array
  const updatedMoves = [...gameData.moves];
  const moveIndex = parseInt(index, 10);
  
  if (moveIndex >= 0 && moveIndex < updatedMoves.length) {
    updatedMoves[moveIndex] = {
      ...updatedMoves[moveIndex],
      ...updates
    };
  }
  
  return {
    ...gameData,
    moves: updatedMoves
  };
};

/**
 * Check if position data has analysis
 * @param {Object} gameData - The game data object
 * @param {string} index - The move index
 * @returns {boolean} Whether the position has analysis data
 */
export const hasPositionAnalysis = (gameData, index) => {
  const positionData = getPositionData(gameData, index);
  return !!(positionData?.analysis?.pvs?.length > 0 || positionData?.stockfish);
};

/**
 * Get analysis data for a position
 * @param {Object} gameData - The game data object
 * @param {string} index - The move index
 * @returns {Object|null} Analysis data or null if not available
 */
export const getPositionAnalysis = (gameData, index) => {
  const positionData = getPositionData(gameData, index);
  return positionData?.analysis || positionData?.stockfish || null;
};

/**
 * Create a default startPosition structure from a FEN
 * @param {string} fen - The FEN string
 * @returns {Object} Default startPosition structure
 */
export const createStartPosition = (fen) => {
  return {
    fen: fen || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1',
    content: "",
    arrows: [],
    highlightedSquares: {},
    analysis: { pvs: [], scores: [], classification: "", depth: 0 },
    variations: []  // Add variations support for starting position
  };
}; 